import React, {
  Fragment,
  useCallback,
  useRef,
  useEffect,
  useState,
} from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useNavigate } from "react-router-dom";
import { useHandleOpenViewerClick } from "../../../media-viewer/mediaViewerRedux";
import clsx from "clsx";
import { useContentUpdate } from "../../content/contentServices";
import { capitalize } from "../../../util/stringFormat";
import { isBlank, notBlank } from "../../../util/ext";
import { useMediaTable } from "../../common/use-mediaTable";
import {
  getContentGroupsViewPath,
  isApproved,
  isApprovedOrPending,
  isRejected,
  useView,
} from "../organizeUtil";
import { getIds } from "../../../util/mediaUtil";
import { equals, find, findIndex, intersection, isEmpty, propEq } from "ramda";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInbox } from "@fortawesome/pro-thin-svg-icons";
import { useSelected } from "../OrganizeProvider";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";

import CustomTablePagination from "../../common/CustomTablePagination";
import DraggableRow from "./DraggableRow";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import { useIsIapReviewer } from "../../../auth/authUtils";

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: "20px",
    position: "relative",
  },

  largeCell: {
    width: "37.5%",
  },

  smallCell: {
    width: "5%",
  },

  medCell: {
    width: "9%",
  },

  medReviewCell: {
    width: "12%",
  },

  container: {
    height: "calc(100vh - 390px)",
    padding: 0,
  },

  containerWithHeader: {
    height: "calc(100vh - 480px)",
  },

  empty: {
    fontWeight: 600,
  },

  inbox: {
    color: theme.palette.primary.darker,
  },

  includedInCell: {
    paddingLeft: "15px",
    width: "15%",
  },

  reviewTitle: {
    paddingLeft: theme.spacing(6),
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

export const EmptyTableText = ({ status }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const showIcon = useMediaQuery("(min-height: 690px)");

  const handleClick = () => navigate(getContentGroupsViewPath());

  return (
    <Grid
      className={classes.container}
      style={{ paddingTop: 75 }}
      container
      direction="column"
      alignItems="center"
      spacing={2}
    >
      {showIcon && (
        <Grid item>
          <FontAwesomeIcon className={classes.inbox} icon={faInbox} size="8x" />
        </Grid>
      )}

      <Grid item>
        <Typography className={classes.empty}>
          No {status} files at this time
        </Typography>
      </Grid>

      <Grid item>
        <Button
          onClick={handleClick}
          color="primary"
          variant="contained"
          style={{ width: "auto" }}
          aria-label="Go to Organize home screen"
        >
          Start Organizing
        </Button>
      </Grid>
    </Grid>
  );
};

const OrganizeReviewTable = props => {
  const {
    contentList,
    currentStatus,
    hasHeader,
    hasRemove,
    loading,
    onContentEditClick,
    onItemsDelete,
    onPageChange,
    onScrollVisible,
    onSortChange,
    onRowsPerPageChange,
    order,
    orderBy,
    page,
    rowsPerPage,
    searchState,
    sourceFilter,
    total,
    SnackAlertHandler,
  } = props;

  const classes = useStyles();
  const { showAlert, closeAlert, SnackAlert } = SnackAlertHandler;
  const view = useView();
  const isIapReviewer = useIsIapReviewer();

  const { appendToSelectedContent, selectedContent } = useSelected();

  const [contentListClone, setContentListClone] = useState([]);
  useEffect(() => {
    setContentListClone(contentList);
  }, [contentList]);

  const [mostRecentPending, setMostRecentPending] = useState({
    id: null,
    status: null,
  });

  const updateContent = useContentUpdate();
  const handlePendingClick = useCallback(
    async (id, newStatus) => {
      try {
        if (isBlank(id)) {
          throw new Error("No id was provided.");
        }
        // Update Content
        const update = { status: newStatus };
        await updateContent.mutateAsync({
          resourceIds: { id: id },
          data: update,
        });

        // Update Clone of Content List
        const current = find(propEq("id", id), contentListClone);

        setMostRecentPending({ id: id, status: current.status });

        const index = findIndex(propEq("id", id), contentListClone);

        contentListClone[index]["status"] = newStatus;

        showAlert("info", [`Item ${capitalize(newStatus)}`], true);
      } catch (e) {
        showAlert("error", [e.message]);
      }
    },
    [setMostRecentPending, showAlert, contentListClone, updateContent],
  );

  const handleUndo = useCallback(async () => {
    try {
      const update = { status: mostRecentPending.status };
      await updateContent.mutateAsync({
        resourceIds: { id: mostRecentPending.id },
        data: update,
      });

      closeAlert();
      showAlert("info", ["Action Undone"]);
    } catch (e) {
      showAlert("error", [e.message]);
    }
  }, [closeAlert, mostRecentPending, showAlert, updateContent]);

  const {
    handleRequestSort,
    handleChangePage,
    handleUpdateRowsPerPage,
  } = useMediaTable(
    {
      onSortChange,
      onPageChange,
      onRowsPerPageChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
      page,
      rowsPerPage,
      editId: null,
    },
  );

  const { handleOpenViewerClick, isViewerLoading } = useHandleOpenViewerClick();

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl) && notBlank(contentList)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible, contentList]);

  //-- Manage Checkbox State
  const [checkbox, setCheckbox] = useState({
    checked: false,
    indeterminate: false,
  });
  useEffect(() => {
    const selectedIdsForPage = intersection(
      getIds(contentList),
      getIds(selectedContent),
    );
    if (!isEmpty(selectedIdsForPage)) {
      setCheckbox({
        checked: equals(selectedIdsForPage.length, contentList.length),
        indeterminate: !equals(selectedIdsForPage.length, contentList.length),
      });
    } else {
      setCheckbox({ checked: false, indeterminate: false });
    }
  }, [contentList, selectedContent]);

  const handleChange = event => appendToSelectedContent(checkbox, contentList);

  return (
    <Paper elevation={0} className={classes.root}>
      {loading && <PanelLoadingIndicator transparent />}
      {total === 0 ? (
        <EmptyTableText status={currentStatus} />
      ) : (
        <Fragment>
          <SnackAlert
            action={
              <Button
                variant="outlined"
                style={{ color: "white" }}
                onClick={handleUndo}
              >
                Undo
              </Button>
            }
          />
          <TableContainer
            ref={tableContainerEl}
            className={clsx(classes.container, {
              [classes.containerWithHeader]: hasHeader,
            })}
          >
            <Table stickyHeader aria-label="Content">
              <TableHead>
                <TableRow>
                  {isApprovedOrPending(view) && (
                    <TableCell
                      padding="checkbox"
                      align="center"
                      className={classes.smallCell}
                    >
                      <Checkbox
                        color="primary"
                        indeterminate={checkbox.indeterminate}
                        checked={checkbox.checked}
                        onChange={handleChange}
                        inputProps={{ "aria-label": "Select" }}
                      />
                    </TableCell>
                  )}
                  <TableCell
                    key="content"
                    align="left"
                    className={classes.largeCell}
                    classes={{
                      root: classes.reviewTitle,
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === "title"}
                      direction={order}
                      onClick={() => handleRequestSort("title")}
                    >
                      Title
                      {orderBy === "title" ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  {isRejected(view) && (
                    <TableCell
                      key="rejected-note"
                      align="left"
                      className={classes.medReviewCell}
                    >
                      Note
                    </TableCell>
                  )}

                  <TableCell
                    key="date-added"
                    align="left"
                    className={classes.medReviewCell}
                  >
                    <TableSortLabel
                      active={orderBy === "media.auditAdded"}
                      direction={order}
                      onClick={() => handleRequestSort("media.auditAdded")}
                    >
                      Uploaded
                      {orderBy === "media.auditAdded" ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    key="content-type"
                    align="left"
                    className={classes.medReviewCell}
                  >
                    <TableSortLabel
                      active={orderBy === "type"}
                      direction={order}
                      onClick={() => handleRequestSort("type")}
                    >
                      Type
                      {orderBy === "type" ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>

                  {!isIapReviewer && (
                    <TableCell
                      key="origin"
                      align="left"
                      className={classes.medReviewCell}
                      style={{ paddingLeft: 20 }}
                    >
                      Origin
                    </TableCell>
                  )}

                  {isApproved(view) && !isIapReviewer && (
                    <TableCell
                      key="included-in"
                      align="left"
                      className={classes.includedInCell}
                    >
                      Included In
                    </TableCell>
                  )}

                  <TableCell
                    className={classes.smallCell}
                    style={{ paddingLeft: 12 }}
                    key="action"
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {contentListClone.map((content, index) => (
                  <DraggableRow
                    loading={isViewerLoading}
                    searchState={searchState}
                    sourceFilter={sourceFilter}
                    currentStatus={currentStatus}
                    index={index}
                    key={content.id}
                    content={content}
                    parent={undefined}
                    handleOpenViewerClick={handleOpenViewerClick}
                    hasRemove={hasRemove}
                    onItemsDelete={onItemsDelete}
                    onContentEditClick={onContentEditClick}
                    handlePendingClick={handlePendingClick}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <CustomTablePagination
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onRowsPerPageChange={event =>
              handleUpdateRowsPerPage(+event.target.value)
            }
            onPageChange={(event, page) => {
              // removeAllSelectedContent();
              handleChangePage(page);
            }}
          />
        </Fragment>
      )}
    </Paper>
  );
};

export default OrganizeReviewTable;
